import { useEffect, useRef, useState, type ReactNode } from 'react'
import axios from 'axios'
import { LogIn, LogOut, RefreshCw } from 'lucide-react'
import logo from '@/assets/logo.png'
import { useFeatureDispatch, useFeatureSelector } from '@/platform/state/hooks'
import { platformEnv } from '@/platform/config/env'
import { clearSessionTokens, getValidAccessToken, readClientTokenClaims, redirectToLogin, refreshAccessToken } from './sessionTokens'
import { sessionActions, type SessionUser } from './sessionSlice'
import { FullScreenLoader } from '@/platform/ui/FullScreenLoader'

export function SessionGate({ children }: { children: ReactNode }) {
  const dispatch = useFeatureDispatch()
  const status = useFeatureSelector((state) => state.session.status)
  const forbiddenReason = useFeatureSelector((state) => state.session.forbiddenReason)
  const currentClient = useFeatureSelector((state) => state.session.authorization.clientId)
  const [rechecking, setRechecking] = useState(false)
  const initialized = useRef(false)

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const controller = new AbortController()

    const loadSession = async () => {
      const token = await getValidAccessToken()
      if (!token) return null
      try {
        return await axios.get(`${platformEnv.ssoApiUrl}/v1/auth/me`, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
      } catch (cause) {
        if (!axios.isAxiosError(cause) || cause.response?.status !== 401) throw cause
        const refreshed = await refreshAccessToken()
        if (!refreshed) throw cause
        return axios.get(`${platformEnv.ssoApiUrl}/v1/auth/me`, { signal: controller.signal, headers: { Authorization: `Bearer ${refreshed}` } })
      }
    }

    void loadSession().then(async (response) => {
      if (!response) {
        dispatch(sessionActions.anonymous())
        return
      }
      const user = response.data?.data as SessionUser | undefined
      if (!response.data?.success || !user) throw new Error('Không đọc được phiên đăng nhập.')
      let claims = readClientTokenClaims()
      if (claims?.role !== user.role) {
        const refreshed = await refreshAccessToken()
        if (refreshed) claims = readClientTokenClaims(refreshed)
      }
      const permissions = claims && Array.isArray(claims.permissions)
        ? claims.permissions.filter((item): item is string => typeof item === 'string')
        : []
      const authorization = {
        clientId: claims?.client ?? null,
        permissions,
      }
      if (claims?.client && claims.client !== platformEnv.ssoClientId) {
        dispatch(sessionActions.forbidden({ user, reason: 'wrong-client', authorization }))
        return
      }
      if (permissions.length === 0) {
        dispatch(sessionActions.forbidden({ user, reason: 'no-permissions', authorization }))
        return
      }
      dispatch(sessionActions.authenticated({ user, authorization }))
    }).catch((cause) => {
      if (axios.isCancel(cause)) return
      if (axios.isAxiosError(cause) && cause.response?.status === 401) {
        clearSessionTokens()
        dispatch(sessionActions.anonymous())
        return
      }

      // Fallback: nếu mạng lỗi nhưng token claims có sẵn và hợp lệ (admin / super_admin)
      const claims = readClientTokenClaims()
      if (claims?.sub && claims.client === platformEnv.ssoClientId) {
        const fallbackUser: SessionUser = {
          id: claims.sub,
          email: claims.email || '',
          role: claims.role || 'user',
          display_name: null,
          avatar: null,
        }
        const permissions = Array.isArray(claims.permissions)
          ? claims.permissions.filter((item): item is string => typeof item === 'string')
          : []
        if (permissions.length > 0) {
          dispatch(sessionActions.authenticated({
            user: fallbackUser,
            authorization: { clientId: claims.client ?? null, permissions },
          }))
          return
        }
      }

      dispatch(sessionActions.failed())
    })
    return () => controller.abort()
  }, [dispatch])

  if (status === 'checking') return <FullScreenLoader />
  if (status === 'anonymous') return <StatusScreen
    title="Quản Lý Cửa Hàng"
    action="Đăng nhập"
    actionIcon={<LogIn size={18} aria-hidden="true" />}
    onAction={() => redirectToLogin()}
  />
  if (status === 'forbidden') {
    const isWrongClient = forbiddenReason === 'wrong-client'
    const handleLogout = () => {
      clearSessionTokens()
      dispatch(sessionActions.anonymous())
    }
    const handleSwitchAccount = () => {
      clearSessionTokens()
      redirectToLogin()
    }
    const handleRecheck = async () => {
      setRechecking(true)
      try {
        await refreshAccessToken()
      } catch {
        // ignore
      } finally {
        setRechecking(false)
        window.location.reload()
      }
    }

    if (isWrongClient) {
      return <StatusScreen
        title="Sai ứng dụng đăng nhập"
        description={`Phiên${currentClient ? ` thuộc “${currentClient}”` : ' hiện tại'} không dành cho Shop Admin. Hãy đổi tài khoản hoặc đăng xuất.`}
        action="Đổi tài khoản"
        actionIcon={<LogIn size={16} aria-hidden="true" />}
        onAction={handleSwitchAccount}
        secondary="Đăng xuất"
        secondaryIcon={<LogOut size={16} aria-hidden="true" />}
        onSecondary={handleLogout}
      />
    }

    return <StatusScreen
      title="Bạn chưa có quyền truy cập"
      titleTone="danger"
      descriptionTone="dark"
      description="Tài khoản của bạn chưa được cấp quyền trên Quản Lý Cửa Hàng. Hãy liên hệ quản trị viên để được cấp quyền, sau đó kiểm tra lại hoặc đăng xuất."
      action={rechecking ? 'Đang kiểm tra…' : 'Kiểm tra lại quyền'}
      actionIcon={<RefreshCw size={16} className={rechecking ? 'animate-spin' : undefined} aria-hidden="true" />}
      onAction={handleRecheck}
      secondary="Đăng xuất"
      secondaryIcon={<LogOut size={16} aria-hidden="true" />}
      onSecondary={handleLogout}
      tertiary="Đổi tài khoản khác"
      onTertiary={handleSwitchAccount}
    />
  }
  if (status === 'error') return <StatusScreen
    title="Chưa kết nối được SSO"
    description="Vui lòng thử lại sau ít phút hoặc đăng xuất để đăng nhập lại."
    action="Thử lại"
    onAction={() => window.location.reload()}
    secondary="Đăng xuất"
    secondaryIcon={<LogOut size={16} aria-hidden="true" />}
    onSecondary={() => { clearSessionTokens(); dispatch(sessionActions.anonymous()) }}
  />
  return <>{children}</>
}

function StatusScreen({
  title,
  titleTone,
  description,
  descriptionTone,
  action,
  actionIcon,
  onAction,
  secondary,
  secondaryIcon,
  onSecondary,
  tertiary,
  onTertiary,
}: {
  title: string
  titleTone?: 'danger'
  description?: string
  descriptionTone?: 'dark'
  action?: string
  actionIcon?: ReactNode
  onAction?: () => void
  secondary?: string
  secondaryIcon?: ReactNode
  onSecondary?: () => void
  tertiary?: string
  onTertiary?: () => void
}) {
  return <main className="status-screen"><section className="status-card" aria-label={title || 'Đăng nhập'}>
    <header className="status-brand">
      <span className="status-brand-mark"><img src={logo} alt="" /></span>
      <span className="status-brand-copy"><strong>Học Nhé</strong><small>Học cùng nhau</small></span>
    </header>
    {title && <h1 className={titleTone === 'danger' ? 'status-title-danger' : undefined}>{title}</h1>}
    {description && <p className={`status-description${descriptionTone === 'dark' ? ' status-description-dark' : ''}`}>{description}</p>}
    {(action && onAction || secondary && onSecondary || tertiary && onTertiary) && <div className="status-actions">
      {action && onAction && <button className="primary-button" onClick={onAction}>{actionIcon}{action}</button>}
      {secondary && onSecondary && <button className="secondary-button" onClick={onSecondary}>{secondaryIcon}{secondary}</button>}
      {tertiary && onTertiary && <button type="button" className="secondary-button" onClick={onTertiary}>{tertiary}</button>}
    </div>}
  </section></main>
}
